import { useState, useEffect } from 'react';
import { Plus, Trash2, AlertCircle, CheckCircle, Loader, Play, ChevronDown, ChevronRight } from 'lucide-react';
import { API_BASE, fetchApi, getAuthHeaders } from '../../services/apiClient';

/** An evidence chain: a fixed sequence of table lookups for one entity type,
 *  each step fed by the previous step's real row. The first empty step ends
 *  the chain; its note is the finding. Runs at the END of the SQL seeder. */
interface ChainStep {
  id: number;
  label: string;
  table: string;
  where: [string, string][];
  select: string[];
  order_by?: string | null;
  limit?: number | null;
  if_empty: string;
  if_found?: string | null;
}

interface Chain {
  id: number;
  chain_key: string;
  start_entity_type: string;
  description: string | null;
  steps: ChainStep[];
  enabled: boolean;
  created_by: string;
}

interface StepResult {
  step: number;
  label: string;
  sql: string;
  row_count: number;
  rows: Record<string, unknown>[];
  verdict: 'found' | 'stopped' | 'error' | 'reused';
  note: string;
  error: string | null;
}

interface Draft {
  id: number | null;
  chain_key: string;
  start_entity_type: string;
  description: string;
  steps: ChainStep[];
  enabled: boolean;
}

const blankStep = (id: number): ChainStep => ({
  id,
  label: '',
  table: '',
  where: [['', '']],
  select: [],
  order_by: '',
  limit: 10,
  if_empty: '',
  if_found: '',
});

const blankDraft = (): Draft => ({
  id: null,
  chain_key: '',
  start_entity_type: '',
  description: '',
  steps: [blankStep(1)],
  enabled: true,
});

const VERDICT_STYLE: Record<StepResult['verdict'], string> = {
  found: 'border-green-300 bg-green-50 text-green-800',
  stopped: 'border-amber-300 bg-amber-50 text-amber-800',
  error: 'border-red-300 bg-red-50 text-red-800',
  reused: 'border-gray-300 bg-gray-50 text-gray-700',
};

const inputCls = 'w-full border border-gray-300 rounded px-2 py-1 text-sm';

export default function EvidenceChainsEditor() {
  const [chains, setChains] = useState<Chain[]>([]);
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [testValue, setTestValue] = useState('');
  const [testClient, setTestClient] = useState('');
  const [testing, setTesting] = useState<number | null>(null);
  const [results, setResults] = useState<Record<number, StepResult[]>>({});

  useEffect(() => {
    load();
  }, []);

  const flash = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const readError = async (response: Response) => {
    const text = await response.text();
    return text || response.statusText;
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const [cr, er] = await Promise.all([
        fetchApi(`${API_BASE}/admin/evidence-chains`, { headers }),
        fetchApi(`${API_BASE}/issue-analysis/entity-types`, { headers }),
      ]);
      if (!cr.ok) throw new Error(await readError(cr));
      setChains(await cr.json());
      if (er.ok) {
        const body = await er.json();
        setEntityTypes((body.entity_types ?? []).map((t: { name: string }) => t.name));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (c: Chain | null) => {
    setError('');
    setJsonMode(false);
    setDraft(
      c
        ? {
            id: c.id,
            chain_key: c.chain_key,
            start_entity_type: c.start_entity_type,
            description: c.description ?? '',
            steps: c.steps.map((s) => ({ ...s, where: s.where.map(([a, b]) => [a, b] as [string, string]) })),
            enabled: c.enabled,
          }
        : blankDraft(),
    );
  };

  const toggleJson = () => {
    if (!draft) return;
    if (!jsonMode) {
      setJsonText(JSON.stringify(cleanSteps(draft.steps), null, 2));
      setJsonMode(true);
      return;
    }
    try {
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) throw new Error('steps must be a JSON array');
      setDraft({ ...draft, steps: parsed.map(normaliseStep) });
      setJsonMode(false);
      setError('');
    } catch (e) {
      setError(`JSON is not valid yet: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const normaliseStep = (s: Partial<ChainStep>, i: number): ChainStep => ({
    id: s.id ?? i + 1,
    label: s.label ?? '',
    table: s.table ?? '',
    where: (s.where ?? []).map(([a, b]) => [a, b] as [string, string]),
    select: s.select ?? [],
    order_by: s.order_by ?? '',
    limit: s.limit ?? 10,
    if_empty: s.if_empty ?? '',
    if_found: s.if_found ?? '',
  });

  const cleanSteps = (steps: ChainStep[]) =>
    steps.map((s, i) => {
      const out: Record<string, unknown> = {
        id: i + 1,
        label: s.label.trim(),
        table: s.table.trim(),
        where: s.where.filter(([c, v]) => c.trim() && v.trim()).map(([c, v]) => [c.trim(), v]),
        select: s.select.map((c) => c.trim()).filter(Boolean),
        limit: s.limit ?? 10,
        if_empty: s.if_empty.trim(),
      };
      if (s.order_by && s.order_by.trim()) out.order_by = s.order_by.trim();
      if (s.if_found && s.if_found.trim()) out.if_found = s.if_found.trim();
      return out;
    });

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError('');
    try {
      let steps: unknown;
      if (jsonMode) {
        steps = JSON.parse(jsonText);
      } else {
        steps = cleanSteps(draft.steps);
      }
      const headers = { ...(await getAuthHeaders()), 'Content-Type': 'application/json' };
      const body =
        draft.id === null
          ? { chain_key: draft.chain_key.trim(), start_entity_type: draft.start_entity_type.trim(), description: draft.description.trim() || null, steps, enabled: draft.enabled }
          : { start_entity_type: draft.start_entity_type.trim(), description: draft.description.trim() || null, steps, enabled: draft.enabled };
      const url = draft.id === null ? `${API_BASE}/admin/evidence-chains` : `${API_BASE}/admin/evidence-chains/${draft.id}`;
      const response = await fetchApi(url, { method: draft.id === null ? 'POST' : 'PUT', headers, body: JSON.stringify(body) });
      if (!response.ok) throw new Error(await readError(response));
      setDraft(null);
      flash('Chain saved. It applies to the very next ticket.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const setEnabled = async (c: Chain, enabled: boolean) => {
    setError('');
    try {
      const headers = { ...(await getAuthHeaders()), 'Content-Type': 'application/json' };
      const response = await fetchApi(`${API_BASE}/admin/evidence-chains/${c.id}`, { method: 'PUT', headers, body: JSON.stringify({ enabled }) });
      if (!response.ok) throw new Error(await readError(response));
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const testRun = async (c: Chain) => {
    setTesting(c.id);
    setError('');
    try {
      const headers = { ...(await getAuthHeaders()), 'Content-Type': 'application/json' };
      const response = await fetchApi(`${API_BASE}/admin/evidence-chains/${c.id}/run`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ value: testValue.trim(), client: testClient.trim() }),
      });
      if (!response.ok) throw new Error(await readError(response));
      const data: StepResult[] = await response.json();
      setResults((r) => ({ ...r, [c.id]: data }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setTesting(null);
    }
  };

  const updateStep = (i: number, patch: Partial<ChainStep>) => {
    if (!draft) return;
    setDraft({ ...draft, steps: draft.steps.map((s, k) => (k === i ? { ...s, ...patch } : s)) });
  };

  const renderForm = () => {
    if (!draft) return null;
    return (
      <div className="bg-white border border-blue-300 rounded-lg p-4 mb-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">{draft.id === null ? 'New chain' : `Edit chain: ${draft.chain_key}`}</h3>
          <button onClick={toggleJson} className="text-sm text-blue-700 hover:underline">
            {jsonMode ? 'Back to the form' : 'Advanced: edit as JSON'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="text-sm">
            Chain key
            <input className={inputCls} value={draft.chain_key} disabled={draft.id !== null} placeholder="booking_to_bl_flow"
              onChange={(e) => setDraft({ ...draft, chain_key: e.target.value })} />
          </label>
          <label className="text-sm">
            Starts from entity type
            <input className={inputCls} list="chain-entity-types" value={draft.start_entity_type}
              onChange={(e) => setDraft({ ...draft, start_entity_type: e.target.value })} />
            <datalist id="chain-entity-types">{entityTypes.map((t) => <option key={t} value={t} />)}</datalist>
          </label>
          <label className="text-sm flex items-end gap-2">
            <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
            Enabled
          </label>
        </div>
        <label className="text-sm block">
          Description
          <input className={inputCls} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
        </label>
        <p className="text-xs text-gray-500">
          Values: <code>{'{entity}'}</code> = the ticket's value; <code>{'{N.column}'}</code> = a column of step N's first row;{' '}
          <code>{'{N.column|all}'}</code> = every row of step N (IN list, max 20); <code>{'{entity|after_last: }'}</code> /{' '}
          <code>{'{entity|before_last:_}'}</code> cut text around one character. Rows per step are capped at 20.
        </p>
        {jsonMode ? (
          <textarea className={`${inputCls} font-mono h-72`} value={jsonText} onChange={(e) => setJsonText(e.target.value)} spellCheck={false} />
        ) : (
          <div className="space-y-3">
            {draft.steps.map((s, i) => (
              <div key={i} className="border border-gray-200 rounded p-3 space-y-2 bg-gray-50">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Step {i + 1}</span>
                  <button className="text-red-600 p-1" title="Remove step" disabled={draft.steps.length === 1}
                    onClick={() => setDraft({ ...draft, steps: draft.steps.filter((_, k) => k !== i) })}><Trash2 size={14} /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input className={inputCls} placeholder="Label" value={s.label} onChange={(e) => updateStep(i, { label: e.target.value })} />
                  <input className={inputCls} placeholder="Table (db.dbo.table)" value={s.table} onChange={(e) => updateStep(i, { table: e.target.value })} />
                </div>
                {s.where.map(([c, v], k) => (
                  <div key={k} className="flex gap-2">
                    <input className={inputCls} placeholder="match column" value={c}
                      onChange={(e) => updateStep(i, { where: s.where.map((w, x) => (x === k ? [e.target.value, w[1]] : w)) as [string, string][] })} />
                    <input className={inputCls} placeholder="value, e.g. {entity} or {1.column}" value={v}
                      onChange={(e) => updateStep(i, { where: s.where.map((w, x) => (x === k ? [w[0], e.target.value] : w)) as [string, string][] })} />
                    <button className="text-red-600 p-1" onClick={() => updateStep(i, { where: s.where.filter((_, x) => x !== k) })}><Trash2 size={14} /></button>
                  </div>
                ))}
                <button className="text-sm text-blue-700" onClick={() => updateStep(i, { where: [...s.where, ['', '']] })}>+ match condition</button>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <input className={inputCls} placeholder="Columns to read (comma separated, empty = all)" value={s.select.join(', ')}
                    onChange={(e) => updateStep(i, { select: e.target.value.split(',').map((x) => x.trim()) })} />
                  <input className={inputCls} placeholder="Order by, e.g. dateup DESC" value={s.order_by ?? ''} onChange={(e) => updateStep(i, { order_by: e.target.value })} />
                  <input className={inputCls} type="number" min={1} max={20} placeholder="Limit" value={s.limit ?? 10}
                    onChange={(e) => updateStep(i, { limit: Number(e.target.value) || 10 })} />
                </div>
                <textarea className={inputCls} rows={2} placeholder="If the step finds NOTHING, this means… (written by an analyst)" value={s.if_empty}
                  onChange={(e) => updateStep(i, { if_empty: e.target.value })} />
                <textarea className={inputCls} rows={2} placeholder="If the step finds rows, read them like this… (optional)" value={s.if_found ?? ''}
                  onChange={(e) => updateStep(i, { if_found: e.target.value })} />
              </div>
            ))}
            <button className="flex items-center gap-1 text-sm text-blue-700" onClick={() => setDraft({ ...draft, steps: [...draft.steps, blankStep(draft.steps.length + 1)] })}>
              <Plus size={14} /> Add step
            </button>
          </div>
        )}
        <div className="flex gap-2">
          <button onClick={save} disabled={saving} className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm disabled:opacity-50">
            {saving ? 'Saving…' : 'Save chain'}
          </button>
          <button onClick={() => { setDraft(null); setError(''); }} className="px-4 py-1.5 border border-gray-300 rounded text-sm">Cancel</button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-gray-900">Evidence Chains</h2>
        <button onClick={() => startEdit(null)} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-sm">
          <Plus size={14} /> New chain
        </button>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        A chain is a fixed sequence of table lookups for one entity type, each step fed by the previous step's real row. It runs at the end of
        SQL seeding. The first empty step stops the chain, and the note you write for that step is the finding. Edits apply to the next ticket.
      </p>
      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 text-red-800 rounded text-sm flex gap-2"><AlertCircle size={16} /><span className="whitespace-pre-wrap">{error}</span></div>}
      {success && <div className="mb-3 p-3 bg-green-50 border border-green-200 text-green-800 rounded text-sm flex gap-2"><CheckCircle size={16} />{success}</div>}
      {renderForm()}
      {loading ? (
        <div className="flex items-center gap-2 text-gray-500"><Loader size={16} className="animate-spin" /> Loading…</div>
      ) : chains.length === 0 ? (
        <p className="text-sm text-gray-500">No chains yet. Use “New chain”.</p>
      ) : (
        <div className="space-y-2">
          {chains.map((c) => (
            <div key={c.id} className="bg-white border border-gray-200 rounded-lg">
              <div className="flex items-center gap-3 px-4 py-2">
                <button onClick={() => setExpanded(expanded === c.id ? null : c.id)} className="text-gray-500">
                  {expanded === c.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                <div className="flex-1">
                  <div className="font-medium text-sm text-gray-900">{c.chain_key}</div>
                  <div className="text-xs text-gray-500">{c.start_entity_type} · {c.steps.length} step(s){c.description ? ` · ${c.description}` : ''}</div>
                </div>
                <label className="flex items-center gap-1 text-sm">
                  <input type="checkbox" checked={c.enabled} onChange={(e) => setEnabled(c, e.target.checked)} /> On
                </label>
                <button onClick={() => startEdit(c)} className="text-sm text-blue-700">Edit</button>
              </div>
              {expanded === c.id && (
                <div className="border-t border-gray-100 px-4 py-3 space-y-3">
                  {c.steps.map((s) => (
                    <div key={s.id} className="text-sm">
                      <div className="font-medium">{s.id}. {s.label || s.table}</div>
                      <div className="text-xs text-gray-600 font-mono">{s.table} where {s.where.map(([a, b]) => `${a} = ${b}`).join(' AND ')}</div>
                      <div className="text-xs text-gray-600">Reads: {s.select.length ? s.select.join(', ') : 'all columns'}</div>
                      <div className="text-xs text-amber-800">If empty: {s.if_empty || '—'}</div>
                      {s.if_found && <div className="text-xs text-green-800">If found: {s.if_found}</div>}
                    </div>
                  ))}
                  <div className="border-t border-gray-100 pt-3">
                    <div className="text-sm font-medium mb-1">Test run (live, read-only, same path as a ticket)</div>
                    <div className="flex flex-wrap gap-2 items-center">
                      <input className="border border-gray-300 rounded px-2 py-1 text-sm" placeholder={`A real ${c.start_entity_type} value`} value={testValue} onChange={(e) => setTestValue(e.target.value)} />
                      <input className="border border-gray-300 rounded px-2 py-1 text-sm w-40" placeholder="Client (JIRA project key)" value={testClient} onChange={(e) => setTestClient(e.target.value)} />
                      <button onClick={() => testRun(c)} disabled={testing === c.id || !testValue.trim() || !testClient.trim()}
                        className="flex items-center gap-1 px-3 py-1 bg-gray-800 text-white rounded text-sm disabled:opacity-50">
                        {testing === c.id ? <Loader size={14} className="animate-spin" /> : <Play size={14} />} Run
                      </button>
                    </div>
                    {(results[c.id] ?? []).map((r) => (
                      <div key={r.step} className={`mt-2 border rounded p-2 text-xs ${VERDICT_STYLE[r.verdict]}`}>
                        <div className="font-medium">Step {r.step}: {r.label} — {r.verdict.toUpperCase()} ({r.row_count} row(s))</div>
                        <pre className="whitespace-pre-wrap font-mono mt-1">{r.sql}</pre>
                        <div className="mt-1">{r.error ?? r.note}</div>
                        {r.rows.length > 0 && (
                          <div className="overflow-x-auto mt-1">
                            <table className="text-xs bg-white text-gray-800 border border-gray-200">
                              <thead><tr>{Object.keys(r.rows[0]).map((k) => <th key={k} className="px-2 py-0.5 border-b text-left">{k}</th>)}</tr></thead>
                              <tbody>{r.rows.map((row, ri) => <tr key={ri}>{Object.values(row).map((v, vi) => <td key={vi} className="px-2 py-0.5 border-b">{String(v ?? '')}</td>)}</tr>)}</tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
